import { Accordion, Badge, Group, Paper, Stack, Table, Text, Title } from '@mantine/core';

import { SupplierSectionNav } from '../components/supplier-section-nav';
import { formatMinorAmount } from '../features/suppliers/supplier-utils';
import { apiHook } from '../treaty';

export function SupplierCatalogPage() {
  const catalogQuery = apiHook.useQuery((client) => client['product-catalog'].get());
  return (
    <Stack gap='lg' maw={1250} mx='auto'>
      <div>
        <Title order={1}>Product catalog</Title>
        <Text c='dimmed' mt={5}>
          Structured products, exact variants, and latest supplier prices.
        </Text>
      </div>
      <SupplierSectionNav />
      {catalogQuery.isLoading && <Text c='dimmed'>Loading catalog…</Text>}
      {!catalogQuery.isLoading && !catalogQuery.data?.length && (
        <Paper withBorder radius='lg' p='xl'>
          <Text c='dimmed' ta='center'>
            No products yet. An administrator can create the catalog in Supplier Settings.
          </Text>
        </Paper>
      )}
      <Accordion variant='separated'>
        {catalogQuery.data?.map((product) => (
          <Accordion.Item key={product.id} value={product.id}>
            <Accordion.Control>
              <Group>
                <Text fw={650}>{[product.brand, product.name].filter(Boolean).join(' ')}</Text>
                <Badge variant='light'>{product.category.name}</Badge>
                <Text size='xs' c='dimmed'>
                  {product.variants.length} variants
                </Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack>
                {product.variants.map((variant) => (
                  <Paper key={variant.id} withBorder radius='md' p='md'>
                    <Group justify='space-between'>
                      <div>
                        <Text fw={600}>{variant.name}</Text>
                        <Text size='xs' c='dimmed'>
                          {variant.sku ? `SKU ${variant.sku} · ` : ''}Default unit:{' '}
                          {variant.defaultUnit.symbol}
                        </Text>
                      </div>
                      <Badge color={variant.active ? 'green' : 'gray'}>
                        {variant.active ? 'Active' : 'Archived'}
                      </Badge>
                    </Group>
                    <Table mt='md'>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Supplier</Table.Th>
                          <Table.Th>Latest price</Table.Th>
                          <Table.Th>MOQ</Table.Th>
                          <Table.Th>Tax / freight</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {!variant.currentPrices.length && (
                          <Table.Tr>
                            <Table.Td colSpan={4}>
                              <Text size='sm' c='dimmed'>
                                No supplier prices.
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        )}
                        {variant.currentPrices.map((price: any) => (
                          <Table.Tr key={price.id}>
                            <Table.Td>{price.supplier.name}</Table.Td>
                            <Table.Td>
                              {formatMinorAmount(price.amountMinor, price.currencyCode)} /{' '}
                              {price.priceQuantity} {price.priceUnit.symbol}
                            </Table.Td>
                            <Table.Td>
                              {price.moqQuantity
                                ? `${price.moqQuantity} ${price.moqUnit?.symbol ?? ''}`
                                : '—'}
                            </Table.Td>
                            <Table.Td>
                              {price.taxInclusion} / {price.freightInclusion}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Paper>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Stack>
  );
}
